/**
 * PriorityPartnersStrip — top-of-detail-page banner surfacing every
 * Partner that has flagged this dispensary as a priority, with their
 * brand chip, status (paused is tucked away via the backend filter —
 * we only show active) and per-pairing partner notes.
 *
 * Clicking a chip jumps to `/partners?focus=<id>` which auto-opens the
 * priority drawer for that partner. The strip stays silent (returns
 * null) if the dispensary isn't a priority for anyone — zero visual
 * cost on non-priority accounts.
 */
import React, { useEffect, useState } from "react";
import { Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchDispensaryPriorityPartners, partnerInitials } from "../lib/api";

export function PriorityPartnersStrip({ dispensaryId }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchDispensaryPriorityPartners(dispensaryId)
      .then((data) => {
        if (!cancelled) setRows(data || []);
      })
      .catch(() => !cancelled && setRows([]));
    return () => {
      cancelled = true;
    };
  }, [dispensaryId]);

  if (!rows || rows.length === 0) return null;

  return (
    <section
      data-testid="priority-partners-strip"
      className="panel p-3 border border-amber-500/30 bg-amber-500/5"
    >
      <div className="flex items-center gap-2 mb-2">
        <Zap className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-semibold text-amber-200">
          Priority for {rows.length} partner{rows.length === 1 ? "" : "s"}
        </h3>
      </div>
      <ul className="flex flex-col gap-1.5">
        {rows.map((r) => (
          <li
            key={r.partner_id}
            data-testid={`priority-partner-row-${r.partner_id}`}
            className="flex items-start gap-2 bg-zinc-950/40 rounded px-2 py-1.5"
          >
            <Link
              to={`/partners?focus=${r.partner_id}`}
              className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-mono font-bold shrink-0 shadow-sm hover:scale-110 transition-transform"
              style={{
                background: r.brand_color || "#64748b",
                color: "#0f172a",
              }}
              title={r.partner_name}
            >
              {partnerInitials(r.partner_name)}
            </Link>
            <div className="min-w-0 flex-1">
              <Link
                to={`/partners?focus=${r.partner_id}`}
                className="text-sm font-medium text-zinc-100 hover:text-amber-200 inline-flex items-center gap-1.5"
              >
                {r.partner_name}
              </Link>
              {r.partner_notes && (
                <div className="text-xs text-zinc-400 italic mt-0.5">
                  {r.partner_notes}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default PriorityPartnersStrip;
